import { useMemo, useState } from "react";
import * as FaIcons from "react-icons/fa";
import {
  exportAct1AllGradesReport,
  exportAct2AllGradesReport,
  getCountryActualCounts,
  saveCountryActualCounts,
} from "../../actions/studensts";
import {
  excelAct2TopAchievers,
  excelAllGradesReport,
  excelGrade11Report,
  excelUniversitiesReport,
  excelAllInvoicesReport,
  excelShippingReport,
  excelMoheStatementReport,
  excelRushReport,
  excelMoheReport,
  excelCertificateReceiversReport,
  excelCollegesReport,
  excelSchoolsStateReport,
  excelMissingEnrollmentLettersReport,
} from "../exportToExcel";
import UNAUTHORIZED from "../UNAUTHORIZED";

// ── Who sees what ────────────────────────────────────────────────────────────
// admin, asmaa, abdelrhman, acountant-1 see every report; Agent and Yara also
// see Missing Enrollment Letters; the Rush report has its own list.
const ALL_REPORT_USERS = ["admin", "asmaa", "abdelrhman", "acountant-1"];
const MISSING_ENROLLMENT_USERS = [...ALL_REPORT_USERS, "Agent", "agent", "Yara"];
// Mirrors RUSH_REPORT_USERS in Server/src/components/student/student.routes.js.
const RUSH_REPORT_USERS = ["admin", "asmaa", "abdelrhman", "agent", "Agent", "agent5"];
// Mirrors CERTIFICATE_RECEIVERS_REPORT_USERS (+ admin role) in student.routes.js.
const CERTIFICATE_RECEIVERS_USERS = [...ALL_REPORT_USERS, "alaa", "agent5", "agent", "Agent"];

export const REPORT_USERS = Array.from(
  new Set([...MISSING_ENROLLMENT_USERS, ...RUSH_REPORT_USERS, ...CERTIFICATE_RECEIVERS_USERS])
);

const CATEGORIES = [
  { key: "grades", label: "Grades & Achievers" },
  { key: "finance", label: "Finance" },
  { key: "delivery", label: "Delivery & MOHE" },
  { key: "schools", label: "Schools & Colleges" },
];

// Tailwind needs full class names in source, so each accent is spelled out.
const ACCENTS = {
  blue:    { bar: "bg-blue-700",    icon: "bg-blue-100 text-blue-700",       btn: "bg-blue-700 hover:bg-blue-600",       ring: "focus:ring-blue-400" },
  indigo:  { bar: "bg-indigo-700",  icon: "bg-indigo-100 text-indigo-700",   btn: "bg-indigo-700 hover:bg-indigo-600",   ring: "focus:ring-indigo-400" },
  teal:    { bar: "bg-teal-700",    icon: "bg-teal-100 text-teal-700",       btn: "bg-teal-700 hover:bg-teal-600",       ring: "focus:ring-teal-400" },
  purple:  { bar: "bg-purple-700",  icon: "bg-purple-100 text-purple-700",   btn: "bg-purple-700 hover:bg-purple-600",   ring: "focus:ring-purple-400" },
  green:   { bar: "bg-green-700",   icon: "bg-green-100 text-green-700",     btn: "bg-green-700 hover:bg-green-600",     ring: "focus:ring-green-400" },
  fuchsia: { bar: "bg-fuchsia-700", icon: "bg-fuchsia-100 text-fuchsia-700", btn: "bg-fuchsia-700 hover:bg-fuchsia-600", ring: "focus:ring-fuchsia-400" },
  amber:   { bar: "bg-amber-500",   icon: "bg-amber-100 text-amber-700",     btn: "bg-amber-600 hover:bg-amber-500",     ring: "focus:ring-amber-400" },
  rose:    { bar: "bg-rose-700",    icon: "bg-rose-100 text-rose-700",       btn: "bg-rose-700 hover:bg-rose-600",       ring: "focus:ring-rose-400" },
  orange:  { bar: "bg-orange-600",  icon: "bg-orange-100 text-orange-700",   btn: "bg-orange-700 hover:bg-orange-600",   ring: "focus:ring-orange-400" },
  cyan:    { bar: "bg-cyan-700",    icon: "bg-cyan-100 text-cyan-700",       btn: "bg-cyan-700 hover:bg-cyan-600",       ring: "focus:ring-cyan-400" },
};

const SCHOOL_STATES = [
  { value: "moe_hold", label: "MOE Hold" },
  { value: "moe_validation", label: "MOE Validation" },
  { value: "moe", label: "Sending to MOE" },
  { value: "moe_pending", label: "MOE Pending" },
];

const dateTypeLabel = (o) => (o.dateType === "trial" ? "Trial Date" : "Authentication Date");

// Radio pair shared by All Grades and Grade 11.
function DateTypeToggle({ options, setOption }) {
  return (
    <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs font-medium">
      {[
        { value: "auth", label: "Authentication Date" },
        { value: "trial", label: "Trial Date" },
      ].map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => setOption("dateType", opt.value)}
          className={`flex-1 rounded-md px-2 py-1.5 transition ${
            options.dateType === opt.value
              ? "bg-white shadow text-gray-900"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Report catalogue ─────────────────────────────────────────────────────────
// Each entry drives one card: description, which date it filters by, any extra
// options, and the export call. `access` is the username list allowed to see it.
const REPORTS = [
  {
    id: "top_achievers",
    title: "ACT Top Achievers Report",
    category: "grades",
    icon: FaIcons.FaTrophy,
    accent: "blue",
    access: ALL_REPORT_USERS,
    description:
      "Students whose ACT 1 total score, or any ACT 2 subject score, falls inside the chosen grades range. Filters by Authentication Date (invoice date).",
    dateLabel: "Authentication Date",
    initialOptions: { scoreFrom: "30", scoreTo: "36" },
    renderOptions: (o, setOption, accent) => (
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Grades Range <span className="font-normal text-gray-400">(1–36)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            max="36"
            placeholder="From"
            className={`w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${accent.ring}`}
            value={o.scoreFrom}
            onChange={(e) => setOption("scoreFrom", e.target.value)}
          />
          <span className="text-gray-400 text-sm">–</span>
          <input
            type="number"
            min="1"
            max="36"
            placeholder="To"
            className={`w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${accent.ring}`}
            value={o.scoreTo}
            onChange={(e) => setOption("scoreTo", e.target.value)}
          />
        </div>
      </div>
    ),
    canExport: (o) => o.scoreFrom !== "" && o.scoreTo !== "",
    run: (from, to, o) => excelAct2TopAchievers(from, to, o.scoreFrom, o.scoreTo),
  },
  {
    id: "all_grades",
    title: "All Grades Report",
    category: "grades",
    icon: FaIcons.FaChartBar,
    accent: "indigo",
    access: ALL_REPORT_USERS,
    description:
      "Every score grade for ACT 1 and ACT 2, with a per-country summary. Set each country's real totals with “Country Actual Counts” to fill the % column.",
    dateLabel: dateTypeLabel,
    initialOptions: { dateType: "auth" },
    renderOptions: (o, setOption, accent, ctx) => (
      <>
        <DateTypeToggle options={o} setOption={setOption} />
        <button
          type="button"
          disabled={!ctx.from || !ctx.to || ctx.actualLoading}
          onClick={() => ctx.openActualCounts(ctx.from, ctx.to, o.dateType)}
          className="w-full px-3 py-1.5 border border-amber-400 text-amber-700 bg-amber-50 rounded-lg
                     hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-semibold"
        >
          {ctx.actualLoading ? "Loading countries..." : "Manage Country Actual Counts"}
        </button>
      </>
    ),
    run: (from, to, o) => excelAllGradesReport(from, to, o.dateType),
  },
  {
    id: "grade11",
    title: "Grade 11 Report",
    category: "grades",
    icon: FaIcons.FaGraduationCap,
    accent: "teal",
    access: ALL_REPORT_USERS,
    description:
      "Grade 11 students with a score of 25–36. Inside categories are split into ACT 1 / ACT 2 sheets; outside categories share one sheet.",
    dateLabel: dateTypeLabel,
    initialOptions: { dateType: "auth" },
    renderOptions: (o, setOption) => <DateTypeToggle options={o} setOption={setOption} />,
    run: (from, to, o) => excelGrade11Report(from, to, o.dateType),
  },
  {
    id: "all_invoices",
    title: "All Invoices Report",
    category: "finance",
    icon: FaIcons.FaFileInvoiceDollar,
    accent: "green",
    access: ALL_REPORT_USERS,
    description: "A summary by category plus full student invoice details.",
    dateLabel: "Invoice Date",
    run: (from, to) => excelAllInvoicesReport(from, to),
  },
  {
    id: "shipping",
    title: "Shipping Report",
    category: "finance",
    icon: FaIcons.FaShippingFast,
    accent: "indigo",
    access: ALL_REPORT_USERS,
    description: "Students whose invoice shipping amount is not 0.",
    dateLabel: "Invoice Date",
    run: (from, to) => excelShippingReport(from, to),
  },
  {
    id: "mohe_statement",
    title: "MOHE Statement Report",
    category: "finance",
    icon: FaIcons.FaFileSignature,
    accent: "fuchsia",
    access: ALL_REPORT_USERS,
    description: "Students who paid for a MOHE Statement.",
    dateLabel: "MOHE Statement Payment Date",
    run: (from, to) => excelMoheStatementReport(from, to),
  },
  {
    id: "delivery",
    title: "Delivery Report",
    category: "delivery",
    icon: FaIcons.FaTruck,
    accent: "purple",
    access: ALL_REPORT_USERS,
    description:
      "Public & private universities. When a student's Another Invoice Date is also in range, it is shown alongside.",
    dateLabel: "Invoice Date",
    run: (from, to) => excelUniversitiesReport(from, to),
  },
  {
    id: "mohe",
    title: "MOHE Report",
    category: "delivery",
    icon: FaIcons.FaLandmark,
    accent: "purple",
    access: ALL_REPORT_USERS,
    description:
      "Students in MOHE, MOHE Validation or Done state. Another Invoice Date is shown only when it is also in range; the student count is listed at the top.",
    dateLabel: "Invoice Date",
    run: (from, to) => excelMoheReport(from, to),
  },
  {
    id: "certificate_receivers",
    title: "Certificate Receivers Report",
    category: "delivery",
    icon: FaIcons.FaIdCard,
    accent: "cyan",
    access: CERTIFICATE_RECEIVERS_USERS,
    description:
      "Students transferred to MOHE Validation, with National ID, Student Name, Authentication Date, Receiver Name, Receiver National ID and Receiver Phone.",
    dateLabel: "Receiving Date",
    run: (from, to) => excelCertificateReceiversReport(from, to),
  },
  {
    id: "rush",
    title: "Rush Students Report",
    category: "delivery",
    icon: FaIcons.FaBolt,
    accent: "amber",
    access: RUSH_REPORT_USERS,
    description: "Students marked Rush on their invoice, with a summary by category and by state.",
    dateLabel: "Authentication Date",
    run: (from, to) => excelRushReport(from, to),
  },
  {
    id: "colleges",
    title: "Colleges Report",
    category: "schools",
    icon: FaIcons.FaUniversity,
    accent: "teal",
    access: ALL_REPORT_USERS,
    description:
      "Per-college student counts, students grouped by college (listed once per chosen college), and grade 11 / 12 sheets. Students with only an Another Invoice Date in range are included too.",
    dateLabel: "Invoice Date",
    run: (from, to) => excelCollegesReport(from, to),
  },
  {
    id: "schools",
    title: "Schools Report",
    category: "schools",
    icon: FaIcons.FaSchool,
    accent: "rose",
    access: ALL_REPORT_USERS,
    description:
      "Students in the selected MOE states whose Invoice Date or Another Invoice Date is in range. Shows counts per state and per restricted school; restricted-school students are highlighted in red.",
    dateLabel: "Invoice / Another Invoice Date",
    initialOptions: { states: SCHOOL_STATES.map((s) => s.value) },
    renderOptions: (o, setOption) => (
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">States to include</label>
        <div className="grid grid-cols-2 gap-1.5">
          {SCHOOL_STATES.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer
                         border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                className="accent-rose-700"
                checked={o.states.includes(opt.value)}
                onChange={() =>
                  setOption(
                    "states",
                    o.states.includes(opt.value)
                      ? o.states.filter((s) => s !== opt.value)
                      : [...o.states, opt.value]
                  )
                }
              />
              {opt.label}
            </label>
          ))}
        </div>
        {o.states.length === 0 && (
          <p className="text-xs text-red-500 mt-1">Select at least one state.</p>
        )}
      </div>
    ),
    canExport: (o) => o.states.length > 0,
    run: (from, to, o) => excelSchoolsStateReport(from, to, o.states),
  },
  {
    id: "missing_enrollment",
    title: "Missing Enrollment Letters",
    category: "schools",
    icon: FaIcons.FaEnvelopeOpenText,
    accent: "orange",
    access: MISSING_ENROLLMENT_USERS,
    description:
      "Students in any state whose Enrollment Letter Submit status is No. Each date is shown only when it is in range, with a per-state breakdown at the top.",
    dateLabel: "Invoice / Another Invoice Date",
    run: (from, to) => excelMissingEnrollmentLettersReport(from, to),
  },
];

// ── One report card ──────────────────────────────────────────────────────────
function ReportCard({ report, ctx }) {
  const accent = ACCENTS[report.accent];
  const Icon = report.icon;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [options, setOptions] = useState(report.initialOptions || {});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setOption = (key, value) => setOptions((prev) => ({ ...prev, [key]: value }));
  const dateLabel =
    typeof report.dateLabel === "function" ? report.dateLabel(options) : report.dateLabel;
  const ready = from && to && (!report.canExport || report.canExport(options));
  const category = CATEGORIES.find((c) => c.key === report.category)?.label;

  const handleExport = async () => {
    setLoading(true);
    setDone(false);
    try {
      await report.run(from, to, options);
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${accent.ring}`;

  return (
    <div className="flex flex-col bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition">
      <div className={`h-1.5 ${accent.bar}`} />
      <div className="flex flex-col flex-1 p-5 gap-4">
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${accent.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-800 leading-tight">{report.title}</h3>
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
              {category}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">{report.description}</p>

        <div className="inline-flex self-start items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
          <FaIcons.FaFilter className="w-2.5 h-2.5 text-gray-400" />
          Filtered by <span className="font-semibold">{dateLabel}</span>
        </div>

        <div className="mt-auto flex flex-col gap-3 pt-3 border-t border-gray-100">
          {report.renderOptions && report.renderOptions(options, setOption, accent, { ...ctx, from, to })}
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-xs font-semibold text-gray-600 mb-1">From</span>
              <input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-600 mb-1">To</span>
              <input type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
          <button
            type="button"
            disabled={!ready || loading}
            onClick={handleExport}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold
                        disabled:bg-gray-300 disabled:cursor-not-allowed ${accent.btn}`}
          >
            {loading ? (
              "Exporting..."
            ) : done ? (
              <>
                <FaIcons.FaCheck /> Downloaded
              </>
            ) : (
              <>
                <FaIcons.FaFileExcel /> Export Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Reports() {
  const user = JSON.parse(localStorage.getItem("profile")).value[0];
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  // Country actual-counts editor (opened from the All Grades card)
  const [showActualModal, setShowActualModal] = useState(false);
  const [actualLoading, setActualLoading] = useState(false);
  const [actualSaving, setActualSaving] = useState(false);
  // rows: [{ country, act1_actual, act2_actual, act1_sheet, act2_sheet }]
  const [actualRows, setActualRows] = useState([]);

  const available = useMemo(
    () => REPORTS.filter((r) => r.access.includes(user.username)),
    [user.username]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return available.filter(
      (r) =>
        (category === "all" || r.category === category) &&
        (!q || r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q))
    );
  }, [available, category, query]);

  // Fetch the union of countries from the All Grades report (for the chosen
  // date range) and merge with any saved actual counts, then open the editor.
  const openActualCounts = async (dateFrom, dateTo, dateType) => {
    setActualLoading(true);
    try {
      const [resAct1, resAct2, resSaved] = await Promise.all([
        exportAct1AllGradesReport(dateFrom, dateTo, dateType),
        exportAct2AllGradesReport(dateFrom, dateTo, dateType),
        getCountryActualCounts(),
      ]);

      const toTitle = (s) =>
        (s || "")
          .trim()
          .replace(/\s+/g, " ")
          .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

      const countryMap = new Map(); // key → { country, act1_sheet:Set, act2_sheet:Set }
      const addRows = (rows, bucket) => {
        if (!rows) return;
        for (const r of rows) {
          const label = toTitle(r["Country"]) || "Unknown";
          const key = label.toLowerCase();
          if (!countryMap.has(key))
            countryMap.set(key, { country: label, act1_sheet: new Set(), act2_sheet: new Set() });
          countryMap.get(key)[bucket].add(r["National ID"]);
        }
      };
      addRows(resAct1?.data?.data, "act1_sheet");
      addRows(resAct2?.data?.data, "act2_sheet");

      const savedMap = new Map();
      if (resSaved && resSaved.status === 200 && Array.isArray(resSaved.data.data)) {
        for (const r of resSaved.data.data) {
          savedMap.set((r.country || "").trim().toLowerCase(), r);
        }
      }

      const rows = [...countryMap.values()]
        .map(({ country, act1_sheet, act2_sheet }) => {
          const saved = savedMap.get(country.toLowerCase()) || {};
          return {
            country,
            act1_sheet: act1_sheet.size,
            act2_sheet: act2_sheet.size,
            act1_actual: saved.act1_actual ?? "",
            act2_actual: saved.act2_actual ?? "",
          };
        })
        .sort((a, b) => a.country.localeCompare(b.country));

      setActualRows(rows);
      setShowActualModal(true);
    } catch (e) {
      console.error(e);
      alert("Failed to load country counts.");
    } finally {
      setActualLoading(false);
    }
  };

  const saveActualCounts = async () => {
    setActualSaving(true);
    try {
      const payload = actualRows.map((r) => ({
        country: r.country,
        act1_actual: Number(r.act1_actual) || 0,
        act2_actual: Number(r.act2_actual) || 0,
      }));
      const res = await saveCountryActualCounts(payload);
      if (res && res.status === 200) {
        setShowActualModal(false);
      } else {
        alert("Failed to save actual counts.");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to save actual counts.");
    } finally {
      setActualSaving(false);
    }
  };

  const updateActual = (idx, key, value) =>
    setActualRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });

  if (available.length === 0) return <UNAUTHORIZED />;

  const ctx = { openActualCounts, actualLoading };

  return (
    <div className="px-4 pb-10">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-indigo-800 text-white p-6 shadow-lg mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              <FaIcons.FaChartLine className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Reports</h1>
              <p className="text-sm text-indigo-100">
                Choose a date range on any report and export it to Excel.
              </p>
            </div>
          </div>
          <span className="rounded-full bg-white/15 px-3 py-1 text-sm font-medium">
            {available.length} report{available.length === 1 ? "" : "s"} available
          </span>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <FaIcons.FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="search"
              placeholder="Search reports..."
              className="w-full rounded-full bg-white text-gray-800 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[{ key: "all", label: "All" }, ...CATEGORIES]
              .filter((c) => c.key === "all" || available.some((r) => r.category === c.key))
              .map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    category === c.key
                      ? "bg-white text-indigo-800"
                      : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  {c.label}
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Cards */}
      {shown.length === 0 ? (
        <div className="text-center text-gray-500 py-16 bg-white rounded-2xl border">
          No reports match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {shown.map((r) => (
            <ReportCard key={r.id} report={r} ctx={ctx} />
          ))}
        </div>
      )}

      {/* Country Actual Counts Editor Modal */}
      {showActualModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-60"
            onClick={() => !actualSaving && setShowActualModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-[720px] max-w-[95vw] max-h-[85vh] z-10 flex flex-col">
            <h2 className="text-lg font-bold text-gray-800 mb-2 border-b pb-3">
              Country Actual Counts
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              Edit the real-world ACT 1 / ACT 2 totals for each country found in the Sheet.
              The Country summary will use these values to compute the % column.
            </p>
            <div className="overflow-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-indigo-700 text-white sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Country</th>
                    <th className="px-3 py-2 text-center">ACT 1 (Sheet)</th>
                    <th className="px-3 py-2 text-center">ACT 1 Actual</th>
                    <th className="px-3 py-2 text-center">ACT 2 (Sheet)</th>
                    <th className="px-3 py-2 text-center">ACT 2 Actual</th>
                  </tr>
                </thead>
                <tbody>
                  {actualRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-gray-500 py-6">
                        No countries found in the Sheet for the selected date range.
                      </td>
                    </tr>
                  ) : (
                    actualRows.map((row, idx) => (
                      <tr key={row.country} className={idx % 2 === 0 ? "bg-indigo-50" : "bg-white"}>
                        <td className="px-3 py-1.5 font-medium text-gray-800">{row.country}</td>
                        <td className="px-3 py-1.5 text-center text-gray-700">{row.act1_sheet}</td>
                        <td className="px-3 py-1.5 text-center">
                          <input
                            type="number"
                            min="0"
                            value={row.act1_actual}
                            onChange={(e) => updateActual(idx, "act1_actual", e.target.value)}
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-center
                                       focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-center text-gray-700">{row.act2_sheet}</td>
                        <td className="px-3 py-1.5 text-center">
                          <input
                            type="number"
                            min="0"
                            value={row.act2_actual}
                            onChange={(e) => updateActual(idx, "act2_actual", e.target.value)}
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-center
                                       focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                disabled={actualSaving}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg
                           hover:bg-gray-200 text-sm font-medium"
                onClick={() => setShowActualModal(false)}
              >
                Close
              </button>
              <button
                disabled={actualSaving || actualRows.length === 0}
                className="px-4 py-2 bg-indigo-700 text-white rounded-lg hover:bg-indigo-600
                           disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                onClick={saveActualCounts}
              >
                {actualSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
